import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import {setWindowTitle} from 'BenchlingUtil';
import * as RegistryConstants from 'constants/RegistryConstants';
import {SavingState} from 'constants/RegistryConstants';
import {Printer, PrinterForCreate} from 'constants/SampleConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as LabelPrintingActions from 'label-printing/LabelPrintingActions';
import navigateToURL from 'lib/navigateToURL';
import * as RegistryUtil from 'registry/RegistryUtil';
import * as StorableActions from 'samples/StorableActions';
import StorableStore from 'samples/StorableStore';
import PrinterSettings from 'settings/label-printing/PrinterSettings';
import SettingsPageContainer from 'settings/SettingsPageContainer';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

import {
  PrinterSettingsPageRegistryQuery,
  PrinterSettingsPageRegistryQueryVariables,
} from './__generated/PrinterSettingsPageRegistryQuery';

const {SAVING_STATES} = RegistryConstants;

export interface PrinterSettingsPageProps {
  printerId?: string;
  ownerUrlId: string;
  registryId: string;
}
interface EnhancedProps {
  savingState: SavingState;
  printer?: Printer;
}
interface QueryingProps {
  isWritable: boolean;
}

function mapStorableStoreToProps(
  storableStore: typeof StorableStore,
  props: PrinterSettingsPageProps
): EnhancedProps {
  return {
    savingState: storableStore.getSavingState(),
    printer: props.printerId ? (storableStore.getPrinter(props.printerId) ?? undefined) : undefined,
  };
}

export const PRINTER_SETTINGS_PAGE_REGISTRY_QUERY = gql`
  query PrinterSettingsPageRegistryQuery($registryId: StrictString!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    registry(id: $registryId) {
      ... on RegistryNode {
        id
        areSettingsAdminOnly
        permissions {
          admin
          writable
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export class PrinterSettingsPage extends React.Component<
  PrinterSettingsPageProps & EnhancedProps & QueryingProps
> {
  override componentDidMount(): void {
    this.fetchPrinters();
  }

  override componentDidUpdate(prevProps: PrinterSettingsPageProps & EnhancedProps): void {
    if (this.props.printerId !== prevProps.printerId) {
      this.fetchPrinters();
    }
    if (
      prevProps.savingState === SAVING_STATES.PENDING &&
      RegistryUtil.isSavingFinished(this.props.savingState)
    ) {
      StorableActions.resetSavingState();
    }
  }

  private fetchPrinters = (): void => {
    LabelPrintingActions.fetchPrinters();
  };

  private handleNavigateToPrinters = (): void => {
    navigateToURL(RegistryUtil.getRegistryPrintersUrl(this.props.ownerUrlId, this.props.registryId));
  };

  private handleCreate = (printer: PrinterForCreate): void => {
    LabelPrintingActions.createPrinter(this.props.registryId, this.props.ownerUrlId, printer);
  };

  private handleUpdate = (printer: Partial<Printer>): void => {
    assert(this.props.printerId != null, 'printerId must exist for update.');
    if (this.props.printerId) {
      LabelPrintingActions.updatePrinter(this.props.printerId, printer);
    }
  };

  override render(): JSX.Element {
    if (this.props.printer) {
      setWindowTitle(`${this.props.printer.name} - Printers`);
    }

    return (
      <SettingsPageContainer
        onClickTitle={this.handleNavigateToPrinters}
        subtitle={this.props.printerId && this.props.printer ? this.props.printer.name : ''}
        title="Printers"
        toolbar={
          this.props.printerId && (
            <ExportAuditLogButton
              ids={[this.props.printerId]}
              buttonSize={ButtonSize.MEDIUM}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
            />
          )
        }
      >
        {this.props.printerId && !this.props.printer ? (
          <HorizontalThrobber />
        ) : (
          <PrinterSettings
            isBeingCreated={!this.props.printerId}
            isWritable={this.props.isWritable}
            printer={this.props.printer}
            onCreate={this.handleCreate}
            onUpdate={this.handleUpdate}
            savingState={this.props.savingState}
          />
        )}
      </SettingsPageContainer>
    );
  }
}

function QueryingPrinterSettingsPage(props: PrinterSettingsPageProps & EnhancedProps): JSX.Element {
  const result = useBenchlingQuery<
    PrinterSettingsPageRegistryQuery,
    PrinterSettingsPageRegistryQueryVariables
  >(PRINTER_SETTINGS_PAGE_REGISTRY_QUERY, {
    variables: {registryId: props.registryId},
  });

  let isWritable = false;
  if (result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.registry)) {
    const registry = result.data.registry;
    isWritable = registry.areSettingsAdminOnly ? registry.permissions.admin : registry.permissions.writable;
  }

  return <PrinterSettingsPage {...props} isWritable={isWritable} />;
}

export default StorableStore.connect(mapStorableStoreToProps)(QueryingPrinterSettingsPage);
